import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import DashboardService from "../services/DashboardService";
import FeedBackService from "../services/FeedbackService";

const feedbackTypes = [
  { label: "Öneri", value: "suggestion", icon: "pi pi-lightbulb", color: "text-amber-400" },
  { label: "Hata", value: "bug", icon: "pi pi-exclamation-triangle", color: "text-rose-400" },
  { label: "İstek", value: "request", icon: "pi pi-star", color: "text-indigo-400" }
];

export default function Dashboard() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
  const navigate = useNavigate();
  const toast = useRef(null);

  const [stats, setStats] = useState({
    todaySales: 0,
    monthSales: 0,
    todayCollections: 0,
    monthCollections: 0,
    todayProfit: 0,
    monthProfit: 0,
    totalCustomers: 0,
    totalProducts: 0,
    inventoryCostValue: 0,
    inventorySaleValue: 0,
    monthlySalesTarget: 0,
    newCustomersCount: 0,
    averageOrderValue: 0,
    recentSales: [],
    criticalStockProducts: [],
    topSellingProducts: [],
    highImpactCustomers: [],
    salesChartData: [],
    upcomingExpenses: []
  });

  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [category, setCategory] = useState(feedbackTypes[0]);
  const [submitted, setSubmitted] = useState(false);
  const maxLength = 500;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await DashboardService.getStats();
      const result = res.data;
      if (result && result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error("Dashboard stats error:", error);
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Veriler yüklenirken bir sorun oluştu.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    try { 
      const res = await FeedBackService.sendFeedback({
        type: category,
        message: feedback
      });
      const result = res.data || res;
      if (result.success === false) {
        toast.current.show({
          severity: "warn",
          summary: "Uyarı",
          detail: "Bildirim gönderilemedi." + (result.message ? " " + result.message : ""),
        }, 3000);
        return;
      } else {
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Bildirim gönderildi. Teşekkürler!",
          life: 3000,
        });
        setSubmitted(true);
        setFeedback("");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const chartData = {
    labels: (stats.salesChartData || []).map(d => d.label),
    datasets: [
      {
        label: 'Satış Hacmi',
        data: (stats.salesChartData || []).map(d => d.value),
        fill: true,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.05)',
        borderWidth: 4,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#10b981',
        pointBorderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 8,
        tension: 0.4
      }
    ]
  };

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 16,
        titleFont: { size: 14, weight: '900' },
        bodyFont: { size: 13, weight: '600' },
        displayColors: false,
        callbacks: {
          label: (context) => `Toplam: ${formatCurrency(context.raw)}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#94a3b8', font: { size: 10, weight: '800' } }
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f1f5f9', borderDash: [6, 6] },
        ticks: {
            color: '#94a3b8',
            font: { size: 10, weight: '800' },
            callback: (value) => formatCurrency(value).replace(',00', '').replace('₺', '') + ' ₺'
        }
      }
    }
  };
const strategyStyles = {
  indigo: {
    glow: "!bg-indigo-500/5",
    iconBg: "!bg-indigo-500",
    iconShadow: "shadow-indigo-500/20",
    progress: "!bg-indigo-500",
  },
  emerald: {
    glow: "!bg-emerald-500/5",
    iconBg: "!bg-emerald-500",
    iconShadow: "shadow-emerald-500/20",
    progress: "!bg-emerald-500",
  },
  blue: {
    glow: "!bg-blue-500/5",
    iconBg: "!bg-blue-500",
    iconShadow: "shadow-blue-500/20",
    progress: "!bg-blue-500",
  },
  amber: {
    glow: "!bg-amber-500/5",
    iconBg: "!bg-amber-500",
    iconShadow: "shadow-amber-500/20",
    progress: "!bg-amber-500",
  },
  default: {
    glow: "!bg-slate-500/5",
    iconBg: "!bg-slate-500",
    iconShadow: "shadow-slate-500/20",
    progress: "bg-slate-500",
  },
};
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);
  };

const StrategyCard = ({ title, value, sub, icon, color, progress }) => {
  const style = strategyStyles[color] || strategyStyles.default;

  return (
    <div className="bg-white p-4 rounded-[3.5rem] shadow-xl shadow-slate-200/40 border border-slate-200 flex flex-col h-full relative overflow-hidden group">
      <div
        className={`absolute top-0 right-0 w-40 h-40 ${style.glow} rounded-full -mr-20 -mt-20 blur-3xl group-hover:scale-125 transition-transform duration-700`}
      />

      <div className="flex items-center gap-6 mb-8 relative z-10">
        <div
          className={`w-24 h-16 rounded-3xl ${style.iconBg} flex items-center justify-center shadow-2xl ${style.iconShadow} group-hover:-rotate-6 transition-transform`}
        >
          <i className={`pi ${icon} text-white text-2xl`} />
        </div>

        <div>
          <div className="text-slate-400 text-[10px] font-black uppercase tracking-[0.25em] mb-1">
            {title}
          </div>
          <div className="text-3xl font-black text-slate-900 tracking-tighter">
            {value}
          </div>
        </div>
      </div>

      <div className="mt-auto relative z-10">
        <div className="flex justify-between items-center mb-3">
          <span className="text-slate-500 text-[12px] font-bold tracking-tight">
            {sub}
          </span>

          {progress !== undefined && (
            <span className="text-slate-900 text-xl font-black">
              {progress}%
            </span>
          )}
        </div>

        {progress !== undefined && (
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${style.progress} transition-all duration-1000`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
const metricStyles = {
  sales: {
    icon: "pi pi-chart-line",
    bg: "!bg-indigo-50",
    iconBg: "!bg-indigo-600",
    text: "!text-indigo-700",
    border: "!border-indigo-300",
    ring: "!ring-indigo-100",
  },
  collections: {
    icon: "pi pi-wallet",
    bg: "!bg-emerald-50",
    iconBg: "!bg-emerald-600",
    text: "!text-emerald-700",
    border: "!border-emerald-300",
    ring: "!ring-emerald-100",
  },
  customers: {
    icon: "pi pi-users",
    bg: "!bg-blue-50",
    iconBg: "!bg-blue-600",
    text: "!text-blue-700",
    border: "!border-blue-300",
    ring: "!ring-blue-100",
  },
  products: {
    icon: "pi pi-box",
    bg: "!bg-amber-50",
    iconBg: "!bg-amber-500",
    text: "!text-amber-700",
    border: "!border-amber-300",
    ring: "!ring-amber-100",
  },
  critical: {
    icon: "pi pi-exclamation-triangle",
    bg: "!bg-rose-50",
    iconBg: "!bg-rose-500",
    text: "!text-rose-700",
    border: "!border-rose-300",
    ring: "!ring-rose-100",
  },
};

const MetricCard = ({ type, title, value, sub }) => {
  const s = metricStyles[type];

  return (
    <div
  className={`relative bg-white border-1 ${s.border}  rounded-[2rem] p-5 shadow-sm hover:shadow-xx transition-all overflow-hidden`}
>
       <div className={`absolute left-0 top-0 h-full w-1 ${s.iconBg}`} />

  <div className="flex items-start justify-between gap-4 pl-3">
        <div>
          <p className="text-[14px] font-black text-slate-700 uppercase  m-0">
            {title}
          </p>
          <h2 className="text-2xl font-black text-slate-900 mt-4 mb-1 ">
            {value}
          </h2>
          <p className="text-xs font-bold text-slate-500 m-0">{sub}</p>
        </div>

        <div className={`w-3 h-12 rounded-2xl ${s.iconBg} flex items-center justify-center shadow-lg`}>
          <i className={`${s.icon} text-white text-lg`} />
        </div>
      </div>
    </div>
  );
};

  return (
    <div className="bg-[#f8fafc] min-h-screen pb-24 font-inter">
  <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

  <div className="max-w-[1600px] mx-auto px-4 md:px-6   space-y-8">

    {/* HEADER */}
    <div className="!bg-slate-300 rounded-[2.5rem] p-5 md:p-5 text-white relative overflow-hidden shadow-2xl">
      <div className="absolute top-0 right-0 w-96 h-96  bg-indigo-500/10 rounded-full blur-3xl -mr-40 -mt-40" />
      

      <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/50 border border-white/10 text-[12px] font-black uppercase tracking-widest text-emerald-800">
            <i className="pi pi-sparkles" />
            KasamBenim Panel
          </span>

          <h1 className="text-xl md:text-5xl font-black text-slate-800 tracking-tight mt-3 mb-3">
            İşletme Özeti
          </h1>

          <p className="text-slate-300 text-sm md:text-base font-medium max-w-2xl text-slate-700 leading-relaxed">
            Satış, tahsilat, müşteri ve stok durumunuzu tek ekrandan takip edin.
          </p>
        </div>

        <div className="grid grid-cols-2 md:flex gap-2">
          <Button label="Hızlı Satış(F2)" icon="pi pi-shopping-cart" className="!bg-emerald-500 !border-none rounded-2xl font-black" onClick={() => navigate("/sales/productsale")} />
          <Button label="Stok Girişi" icon="pi pi-plus-circle" className="!bg-emerald-600 !border-none rounded-2xl font-black" onClick={() => navigate("/stock/adjustment")} />
          <Button label="Müşteriler(F3)" icon="pi pi-users" className="!bg-indigo-500 !border-none rounded-2xl font-black" onClick={() => navigate("/customers")} />
          <Button label="Ürünler" icon="pi pi-box" className="!bg-amber-500 !border-none rounded-2xl font-black" onClick={() => navigate("/products")} />
          <Button label="Giderler" icon="pi pi-wallet" className="!bg-rose-500 !border-none rounded-2xl font-black" onClick={() => navigate("/expenses")} />
        </div>
      </div>
    </div>

    {/* METRICS */}
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5">
      <MetricCard
        type="sales"
        title="Bugünkü Satış"
        value={formatCurrency(stats.todaySales)}
        sub={`Aylık satış: ${formatCurrency(stats.monthSales)}`}
      />

      <MetricCard
        type="collections"
        title="Bugünkü Gelir"
        value={formatCurrency(stats.todayCollections)}
        sub={`Aylık tahsilat: ${formatCurrency(stats.monthCollections)}`}
      />

      <MetricCard
        type="customers"
        title="Müşteri Sayısı"
        value={stats.totalCustomers}
        sub={`${stats.newCustomersCount} yeni kayıt`}
      />

      <MetricCard
        type="products"
        title="Ürün Sayısı"
        value={`${stats.totalProducts}`}
        sub={`Mevcut ürün çeşidi.`}
      />

      <MetricCard
        type="critical"
        title="Kritik Stok"
        value={(stats.criticalStockProducts || []).length}
        sub={"Acil sipariş gerektiren"}
      />
    </div>

    {/* MAIN */}
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">

      {/* LEFT */}
      <div className="xl:col-span-8 space-y-6">

        <Card className="rounded-[2.5rem] border-none shadow-sm overflow-hidden bg-slate-100" pt={{ body: { className: "p-0" } }}>
          <div className="p-4 border-b  border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div>
              <p className="text-[12px] font-black text-emerald-600 uppercase tracking-widest m-0">
                Satış Performansı
              </p>
              <h2 className="text-2xl font-black text-slate-900 mt-2 mb-1">
                Finansal Akış
              </h2>
              <p className="text-sm text-slate-600 font-medium m-0">
                Dönemsel satış hareketlerinizi takip edin.
              </p>
            </div>

            <div className="flex items-center gap-8">
              <div className="text-right">
                <p className="text-[12px] font-black text-slate-600 uppercase tracking-widest m-0">
                  Günlük Kâr
                </p>
                <div className="text-2xl font-black text-emerald-400">
                  {formatCurrency(stats.todayProfit)}
                </div>
              </div>

              <Button
                icon="pi pi-external-link"
                className="p-button-rounded !bg-slate-900 !border-none"
                onClick={() => navigate("/reports/sales")}
              />
            </div>
          </div>

          <div className="p-4 bg-slate-100 h-[420px]">
            <Chart type="line" data={chartData} options={chartOptions} className="w-full  bg-slate-100 h-full" />
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          <Card className="rounded-[2.5rem] border-none bg-slate-100 shadow-sm overflow-hidden " pt={{ body: { className: "p-0" } }}>
            <div className="p-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-black text-slate-900 m-0">Değerli Müşteriler</h3>
                <p className="text-xs text-slate-400 font-bold m-0 mt-1">En yüksek işlem yapan müşteriler</p>
              </div>
              <i className="pi pi-star-fill text-2xl  text-amber-600" />
            </div>

            <div className="p-5 space-y-5">
              {(stats.highImpactCustomers || []).length > 0 ? (
                stats.highImpactCustomers.slice(0, 5).map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-11 h-11 p-4 rounded-2xl bg-slate-300 flex items-center justify-center font-black text-slate-600">
                        {c.name?.charAt(0)}
                      </div>
                      <div>
                       <div className="text-sm font-black text-slate-800 truncate max-w-[180px]">
                        {c.name}
                      </div>
                        <div className="text-[11px] font-bold text-slate-400">{c.orderCount} işlem</div>
                      </div>
                    </div>
                    <div className="text-sm font-black text-slate-900">
                      {formatCurrency(c.totalPurchase)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-10 text-center text-slate-400 text-sm font-bold">
                  Henüz müşteri işlemi bulunamadı.
                </div>
              )}
            </div>
          </Card>

          <Card className="rounded-[2.5rem] border-none shadow-sm overflow-hidden bg-white" pt={{ body: { className: "p-0" } }}>
            <div className="p-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-black text-slate-900 m-0">Stok Uyarıları</h3>
                <p className="text-xs text-slate-400 font-bold m-0 mt-1">Kritik seviyedeki ürünler</p>
              </div>
              <Tag value={(stats.criticalStockProducts || []).length} severity="danger" className="p-3 text-xl rounded-xl" />
            </div>

            <div className="p-3">
              <DataTable value={(stats.criticalStockProducts || []).slice(0, 5)} className="p-datatable-sm no-header">
                <Column field="productName" body={(row) => (
                  <div className="flex flex-col py-2">
                    <span className="text-normal font-black text-slate-800">{row.productName}</span>
                    <span className="text-[10px] font-bold text-slate-400">Kritik stok</span>
                  </div>
                )} />
                <Column body={(row) => (
                  <div className="text-right">
                    <span className="px-3 py-1 rounded-xl bg-rose-50 text-rose-600 text-[11px] font-black">
                      {row.stockQuantity} {row.unit}
                    </span>
                  </div>
                )} />
              </DataTable>
            </div>

            <div className="p-6 border-t border-slate-100 flex gap-2">
              <Button
                label="Stok Hareketleri"
                icon="pi pi-history"
                className="p-button-text flex-1 font-black text-[11px]"
                onClick={() => navigate("/stock/movements")}
              />
               <Button
                label="Hızlı Düzeltme"
                icon="pi pi-plus"
                className="p-button-text flex-1 font-black text-[11px]"
                onClick={() => navigate("/stock/adjustment")}
              />
            </div>
          </Card>

        </div>
      </div>

      {/* RIGHT */}
      <div className="xl:col-span-4 space-y-6">

        <div className="bg-slate-950 rounded-[2.5rem] p-6 text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-56 h-56 bg-emerald-500/10 rounded-full blur-3xl -mr-20 -mt-20" />

          <div className="relative z-10">
            <p className="text-[14px] font-black text-emerald-400 uppercase tracking-widest m-0">
              Finansal Nabız
            </p>

            <div className="mt-5 space-y-2">
              <div className="flex justify-between items-end border-b border-white/10 pb-2">
                <div>
                  <p className="text-[11px] text-slate-400 font-black uppercase m-0">Günlük Tahsilat</p>
                  <div className="text-2xl font-black mt-2">{formatCurrency(stats.todayCollections)}</div>
                </div>
                <i className="pi pi-arrow-up-right text-emerald-400" />
              </div> 

              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[11px] text-slate-400 font-black uppercase m-0">Ortalama Sipariş</p>
                  <div className="text-2xl font-black mt-2">{formatCurrency(stats.averageOrderValue)}</div>
                </div>
                <i className="pi pi-receipt text-amber-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-950 rounded-[2.5rem] p-6 shadow-sm border border-slate-100">
          <div className="mb-6">
            <h3 className="text-xl font-black text-slate-100 m-0">Geri Bildirim</h3>
            <p className="text-xs text-slate-400 font-bold mt-1 m-0">
              KasamBenim’i birlikte geliştirelim.
            </p>
          </div>

          {!submitted ? (
            <div className="flex flex-col gap-3">
              <h3 className="text-xl text-slate-100">
              KATEGORİ: 
            </h3>
              <Dropdown
                value={category}
                options={feedbackTypes}
                optionLabel="label"
                label="Kategori"
                onChange={(e) => setCategory(e.value)}
                className="w-full rounded-2xl"
                itemTemplate={(option) => option && (
                  <div className="flex items-center gap-3">
                    <i className={`${option.icon} ${option.color}`} />
                    <span className="font-black text-xs uppercase text-slate-700">{option.label}</span>
                  </div>
                )} 
              />
            <h3 className="text-xl text-slate-100">
              Mesajınız: 
            </h3>
              <InputTextarea
                value={feedback}
                onChange={(e) => e.target.value.length <= maxLength && setFeedback(e.target.value)}
                rows={5}
                placeholder="Önerinizi, isteğinizi veya hatayı yazın..."
                className="w-full rounded-2xl p-4 text-sm font-medium"
              />

              <div className="flex justify-between text-[11px] text-slate-400 font-bold">
                <span>{feedback.length}/{maxLength}</span>
              </div>

              <Button
                label="Gönder"
                icon="pi pi-send"
                className="w-full !bg-slate-900 !border-none rounded-2xl font-black"
                disabled={!feedback.trim()}
                onClick={handleSubmitFeedback}
              />
            </div>
          ) : (
            <div className="py-12 flex flex-col items-center text-center gap-4">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-500">
                <i className="pi pi-check-circle text-4xl" />
              </div>
              <div className="text-sm font-black text-slate-200 uppercase tracking-widest">İletildi</div>
              <Button label="Yeni Bildirim" className="p-button-text text-slate-900  font-black bg-slate-100" onClick={() => setSubmitted(false)} />
            </div>
          )}
        </div>

      </div>
    </div>
  </div>

  <style>{`
    .p-datatable.no-header .p-datatable-thead { display: none !important; }
    .p-datatable.no-header .p-datatable-tbody > tr { border-bottom: 1px solid #f8fafc !important; }
    .p-datatable.no-header .p-datatable-tbody > tr:last-child { border: none !important; }
  `}</style>
</div>
  );
}
